import { useState } from 'react';
import { SchemaValueInputDialog } from './SchemaValueInputDialog';
import { isSchemaValueReference, SchemaValueBindingModel, SchemaValueModel } from '../../Models/SchemaValueModel';
import { useFlowSchemaContext } from '../Contexts/FlowSchemaContext';
import { resolveFlowSchemaValue, upsertFlowSchemaValue } from '../../Services/FlowSchemaValues';
import { uuid } from '../../../../common/uuid';
import GenericPropertyInput from './GenericPropertyInput';

type SchemaValueInputProps = {
	schemaValue: SchemaValueBindingModel | undefined;
	onSchemaValueChange: (schemaValue: SchemaValueBindingModel | undefined) => void;
	disabled?: boolean;
};

export default function SchemaValueInput({ schemaValue, onSchemaValueChange, disabled }: SchemaValueInputProps) {
	const [open, setOpen] = useState(false);
	const schemaContext = useFlowSchemaContext();
	const sharedSchemas = schemaContext?.flow.SchemaValues ?? [];
	const isReference = isSchemaValueReference(schemaValue);
	const resolved = resolveFlowSchemaValue(schemaContext?.flow, schemaValue);
	const promote = (updated: SchemaValueModel) => {
		if (!schemaContext || isReference) return;
		const id = sharedSchemas.some((schema) => schema.Id === updated.Id) ? uuid() : updated.Id;
		schemaContext.onFlowChanged(upsertFlowSchemaValue(schemaContext.flow, { ...updated, Id: id }));
		onSchemaValueChange({ SchemaValueId: id });
	};
	const detach = (updated: SchemaValueModel) => {
		onSchemaValueChange({ ...updated, Id: uuid() });
	};
	const updateSchema = (updated: SchemaValueModel | undefined) => {
		if (isReference && schemaContext && resolved && updated) {
			schemaContext.onFlowChanged(upsertFlowSchemaValue(schemaContext.flow, { ...updated, Id: resolved.Id }));
		} else {
			onSchemaValueChange(updated);
		}
	};

	return (
		<GenericPropertyInput value={schemaValue} onValueChange={onSchemaValueChange} onOpen={() => setOpen(true)} disabled={disabled}>
			<div
				style={{
					flexGrow: 1,
					overflow: 'hidden',
					whiteSpace: 'wrap',
					textOverflow: 'ellipsis',
				}}
			>
			{resolved?.Value?.title || resolved?.Name || (isReference ? 'Missing shared schema' : '')}
		</div>
		{open && <SchemaValueInputDialog open={open} setOpen={setOpen} schemaValue={resolved} onSchemaValueChange={updateSchema}
			sharedSchemaActions={schemaContext ? {
				schemas: sharedSchemas,
				selectedSchemaId: isReference ? schemaValue.SchemaValueId : undefined,
				onSelect: (id) => onSchemaValueChange({ SchemaValueId: id }),
				onMakeIndependentCopy: isReference && resolved ? detach : undefined,
				onSaveAsSharedSchema: !isReference && resolved ? promote : undefined,
			} : undefined} />}
		</GenericPropertyInput>
	);
}
